import { useState, useCallback, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import type { StepId, PropertyFormData, ContactFormData } from "../types";
import { getStepSchema } from "../config/validationSchemas";
import {
  getNextStepId,
  getPreviousStepId,
  getStepNumber,
  getTotalSteps,
} from "../utils/stepLogic";
import { useEvaluationFormStore } from "@/stores";
import config from "@/config/config";

interface UseFormWizardProps {
  currentStepId: StepId;
  onStepChange: (stepId: StepId) => void;
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

export function useFormWizard({
  currentStepId,
  onStepChange,
  onSuccess,
  onError,
}: UseFormWizardProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [contactData, setContactData] = useState<ContactFormData>({
    first_name: "",
    last_name: "",
    email: "",
    home_phone: "",
    accept_contact: false,
    newsletter: false,
    description: "",
    merkmal: "Bewertung",
  });

  const storeFormData = useEvaluationFormStore((state) => state.formData);
  const updateFormData = useEvaluationFormStore(
    (state) => state.updateFormData
  );
  const addCompletedStep = useEvaluationFormStore(
    (state) => state.addCompletedStep
  );

  // Use specific selectors for address fields to avoid unnecessary re-renders
  const storeStreet = useEvaluationFormStore((state) => state.formData.street);
  const storeCity = useEvaluationFormStore((state) => state.formData.city);
  const storeZipCode = useEvaluationFormStore(
    (state) => state.formData.zip_code
  );
  const storeHouseNumber = useEvaluationFormStore(
    (state) => state.formData.house_number
  );

  // Initialize react-hook-form
  const formMethods = useForm({
    resolver: zodResolver(getStepSchema(currentStepId, storeFormData)),
    mode: "onChange",
    defaultValues: storeFormData as any,
  });

  const {
    control,
    handleSubmit: rhfHandleSubmit,
    formState: { errors },
    watch,
    trigger,
    getValues,
    setValue,
  } = formMethods;

  // Update form values when step changes to ensure fields are registered
  useEffect(() => {
    // Clear errors when step changes
    formMethods.clearErrors();

    // Ensure propertySearchAction field is registered for property-search step
    if (currentStepId === "property-search") {
      const currentValue = formMethods.getValues("propertySearchAction");
      if (currentValue === undefined) {
        formMethods.setValue("propertySearchAction", "", {
          shouldValidate: false,
        });
      }
    }
  }, [currentStepId, formMethods]);

  const formData = watch() as Partial<PropertyFormData>;

  // Track last synced address values to prevent infinite loops
  const lastSyncedAddressRef = useRef<{
    street?: string;
    city?: string;
    zip_code?: string;
  }>({});

  // Sync address fields from store to form whenever they change (CitySearchBox updates this store)
  useEffect(() => {
    // Sync values from store if they exist and are different from last sync
    if (storeStreet && storeCity && storeZipCode) {
      const needsSync =
        lastSyncedAddressRef.current.street !== storeStreet ||
        lastSyncedAddressRef.current.city !== storeCity ||
        lastSyncedAddressRef.current.zip_code !== storeZipCode;

      if (needsSync) {
        // Update last synced values
        lastSyncedAddressRef.current = {
          street: storeStreet,
          city: storeCity,
          zip_code: storeZipCode,
        };

        // Use setValue with shouldValidate to immediately validate
        setValue("city", storeCity, {
          shouldValidate: true,
          shouldDirty: true,
        });
        setValue("street", storeStreet, {
          shouldValidate: true,
          shouldDirty: true,
        });
        setValue("zip_code", storeZipCode, {
          shouldValidate: true,
          shouldDirty: true,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    storeStreet,
    storeCity,
    storeZipCode,
    // setValue is stable from react-hook-form, no need to include in deps
  ]);

  // Calculate step numbers
  const currentStepNumber = getStepNumber(currentStepId, formData);
  const totalSteps = getTotalSteps(formData);

  const handleNext = useCallback(async () => {
    // Ensure address fields are synced from store before validation (CitySearchBox updates this store)
    if (storeStreet && storeCity && storeZipCode) {
      const { setValue } = formMethods;
      const currentValues = getValues();

      // Always sync address fields from store to ensure they're in the form
      setValue("city", storeCity, {
        shouldValidate: false,
        shouldDirty: true,
      });
      setValue("street", storeStreet, {
        shouldValidate: false,
        shouldDirty: true,
      });
      setValue("zip_code", storeZipCode, {
        shouldValidate: false,
        shouldDirty: true,
      });
      setValue("house_number", storeHouseNumber || "", {
        shouldValidate: false,
        shouldDirty: true,
      });

      // Wait a tick to ensure setValue completes
      await new Promise((resolve) => setTimeout(resolve, 0));
    }

    // Now validate all fields
    const isValid = await trigger();

    if (!isValid) {
      // Log validation errors for debugging
      const currentValues = getValues();
      const formErrors = formMethods.formState.errors;
      console.log("Validation failed. Current values:", {
        currentValues,
        errors: formErrors,
        currentStepId,
      });
      return;
    }

    // Update store with current form data
    const currentValues = getValues();
    updateFormData(currentValues);

    // Mark current step as completed
    addCompletedStep(currentStepId);

    // Get next step
    const nextStep = getNextStepId(currentStepId, {
      ...storeFormData,
      ...currentValues,
    });

    if (nextStep) {
      onStepChange(nextStep);
    }
  }, [
    currentStepId,
    storeStreet,
    storeCity,
    storeZipCode,
    storeHouseNumber,
    storeFormData,
    trigger,
    getValues,
    updateFormData,
    addCompletedStep,
    formMethods,
    onStepChange,
  ]);

  const handleBack = useCallback(() => {
    const prevStep = getPreviousStepId(currentStepId, storeFormData);

    if (prevStep) {
      onStepChange(prevStep);
    }
  }, [currentStepId, storeFormData, onStepChange]);

  const handleFinalSubmit = useCallback(async () => {
    // Validate the contact step before submitting
    // Use the contact schema to validate all contact fields
    const contactSchema = getStepSchema("contact", storeFormData);
    const formValues = getValues();

    // Validate contact fields
    const contactValidation = contactSchema.safeParse({
      first_name: formValues.first_name || contactData.first_name || "",
      last_name: formValues.last_name || contactData.last_name || "",
      email: formValues.email || contactData.email || "",
      home_phone: formValues.home_phone || contactData.home_phone || "",
      accept_contact:
        formValues.accept_contact ?? contactData.accept_contact ?? false,
    });

    if (!contactValidation.success) {
      // Set errors for contact fields
      const fieldErrors = contactValidation.error.flatten().fieldErrors;
      Object.keys(fieldErrors).forEach((fieldName) => {
        const errorMessage =
          fieldErrors[fieldName as keyof typeof fieldErrors]?.[0];
        if (errorMessage) {
          formMethods.setError(fieldName as any, {
            type: "validation",
            message: errorMessage,
          });
        }
      });
      // Trigger validation to show errors
      await trigger();
      console.error("Contact validation failed:", contactValidation.error);
      return;
    }

    // Ensure address fields (including house_number) are synced from store before submission
    if (storeStreet && storeCity && storeZipCode) {
      setValue("city", storeCity, { shouldValidate: false, shouldDirty: true });
      setValue("street", storeStreet, {
        shouldValidate: false,
        shouldDirty: true,
      });
      setValue("zip_code", storeZipCode, {
        shouldValidate: false,
        shouldDirty: true,
      });
    }

    // Sync house_number from store
    if (storeHouseNumber) {
      setValue("house_number", storeHouseNumber, {
        shouldValidate: false,
        shouldDirty: true,
      });
    }

    // Also trigger standard validation
    const isValid = await trigger();

    if (!isValid) {
      console.error("Validation failed:", errors);
      return;
    }

    setIsSubmitting(true);

    try {
      // Combine all form data - ensure all critical fields are included
      const currentFormValues = getValues();

      // Explicitly capture propertyFeatures and usage from form values
      // These might not be in the store if they were set directly in the form
      const finalFormData = {
        ...storeFormData,
        ...currentFormValues,
        // Explicitly ensure critical fields are included
        house_number:
          currentFormValues.house_number || storeFormData.house_number || "",
        propertyFeatures:
          currentFormValues.propertyFeatures ||
          storeFormData.propertyFeatures ||
          [],
        usage: currentFormValues.usage || storeFormData.usage || "",
        quality: currentFormValues.quality || storeFormData.quality || "",
        condition: currentFormValues.condition || storeFormData.condition || "",
      };

      // Debug logging
      console.log("=== DEBUG: Final submission payload ===");
      console.log("house_number in finalFormData:", finalFormData.house_number);
      console.log(
        "propertyFeatures in finalFormData:",
        finalFormData.propertyFeatures
      );
      console.log("usage in finalFormData:", finalFormData.usage);
      console.log("quality in finalFormData:", finalFormData.quality);
      console.log("condition in finalFormData:", finalFormData.condition);
      console.log(
        "storeFormData.propertyFeatures:",
        storeFormData.propertyFeatures
      );
      console.log(
        "currentFormValues.propertyFeatures:",
        currentFormValues.propertyFeatures
      );
      console.log("=== END DEBUG ===");

      // Get contact data from react-hook-form (contact step fields)
      const formValues = getValues();
      const contactFromForm = {
        first_name: formValues.first_name || contactData.first_name || "",
        last_name: formValues.last_name || contactData.last_name || "",
        email: formValues.email || contactData.email || "",
        home_phone: formValues.home_phone || contactData.home_phone || "",
        accept_contact:
          formValues.accept_contact ?? contactData.accept_contact ?? false,
        newsletter: formValues.newsletter ?? contactData.newsletter ?? false,
        description: formValues.description || contactData.description || "",
        merkmal: contactData.merkmal || "Bewertung",
      };

      const payload = {
        contactData: contactFromForm,
        formData: finalFormData,
      };

      const response = await fetch(`${config.apiUrl}/api/create-object`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Submission failed");
      }

      onSuccess?.();
    } catch (error) {
      console.error("Submission error:", error);
      onError?.(error as Error);
    } finally {
      setIsSubmitting(false);
    }
  }, [
    storeFormData,
    getValues,
    contactData,
    onSuccess,
    onError,
    trigger,
    errors,
    formMethods,
  ]);

  return {
    // Form state
    control,
    errors,
    formData,
    contactData,
    setContactData,

    // Step state
    currentStepNumber,
    totalSteps,
    isSubmitting,

    // Navigation
    handleNext,
    handleBack,
    handleFinalSubmit,

    // React Hook Form
    rhfHandleSubmit,
    trigger,
    getValues,
    formMethods, // Expose all form methods for FormProvider
  };
}
